import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { 
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription 
} from '../ui/drawer';
import { Eye, Clock, AlertTriangle, Wrench, User, Layers } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoteConsultaDrawer({
  open,
  onOpenChange,
  loteConsultaData,
  loadingConsulta,
}) {
  const handleClose = () => {
    onOpenChange(false);
  };

  // Fetch rastreabilidade arranjo se for lote ARR
  const [rastreabilidade, setRastreabilidade] = useState(null);
  const [loadingRast, setLoadingRast] = useState(false);
  
  const lote = loteConsultaData?.lote;
  const isArranjo = lote?.categoria_codigo === 'ARR' || lote?.origem === 'arranjo';
  const loteId = lote?.id;
  
  useEffect(() => {
    if (!open || !isArranjo || !loteId) {
      setRastreabilidade(null);
      return;
    }
    setLoadingRast(true);
    axios.get(`${API}/lotes/${loteId}/rastreabilidade-arranjo`)
      .then(res => setRastreabilidade(res.data))
      .catch(() => setRastreabilidade(null))
      .finally(() => setLoadingRast(false));
  }, [open, isArranjo, loteId]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader className="border-b border-stone-100 pb-4">
          <DrawerTitle className="text-xl font-heading text-navy flex items-center gap-2">
            {isArranjo ? <Wrench className="h-5 w-5 text-red-600" /> : <Eye className="h-5 w-5 text-blue-600" />}
            {isArranjo ? 'Lote de Arranjo' : 'Consulta de Lote'}
          </DrawerTitle>
          {loteConsultaData && (
            <DrawerDescription className="text-stone-500">
              {loteConsultaData.lote?.codigo_lote || `Lote ${loteConsultaData.lote?.numero_lote}`} •{' '}
              OF {loteConsultaData.of?.numero_of}
              {isArranjo && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                  ARR
                </span>
              )}
            </DrawerDescription>
          )}
        </DrawerHeader>

        <div className="p-4 overflow-y-auto max-h-[70vh]">
          {loadingConsulta ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
            </div>
          ) : loteConsultaData ? (
            <div className="space-y-4">
              {/* Descritivo do trabalho (apenas para Lote ARR) */}
              {isArranjo && (loteConsultaData.lote?.descricao || loteConsultaData.lote?.observacoes) && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4" data-testid="lote-arr-descricao">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="h-4 w-4 text-red-600" />
                    <h4 className="font-bold text-red-800 uppercase text-xs tracking-wide">Trabalho a fazer</h4>
                  </div>
                  <p className="text-base font-semibold text-red-900 leading-relaxed">
                    {loteConsultaData.lote?.descricao || loteConsultaData.lote?.observacoes}
                  </p>
                </div>
              )}
              {/* Resumo do Lote */}
              <div className="bg-stone-50 rounded-xl p-4">
                <h4 className="font-semibold text-navy mb-3">Resumo</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-stone-500">Quantidade:</span>
                    <p className="font-bold text-navy">
                      {loteConsultaData.lote?.quantidade_ajustada || loteConsultaData.lote?.quantidade} un.
                    </p>
                  </div>
                  <div>
                    <span className="text-stone-500">Produzidas:</span>
                    <p className="font-bold text-emerald-600">{loteConsultaData.totalProduzido} un.</p>
                  </div>
                  <div>
                    <span className="text-stone-500">Início:</span>
                    <p className="font-medium text-stone-700">
                      {loteConsultaData.primeiroInicio
                        ? loteConsultaData.primeiroInicio.toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <span className="text-stone-500">Fim:</span>
                    <p className="font-medium text-stone-700">
                      {loteConsultaData.ultimoFim
                        ? loteConsultaData.ultimoFim.toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })
                        : '-'}
                    </p>
                  </div>
                </div>

                {/* Anomalias do Lote (apenas se NÃO for ARR) */}
                {!isArranjo && (loteConsultaData.totalDefeitos > 0 || loteConsultaData.totalArranjos > 0) && (
                  <div className="mt-3 pt-3 border-t border-stone-200">
                    <h5 className="text-xs font-medium text-stone-500 mb-2">Anomalias registadas</h5>
                    <div className="flex items-center gap-3">
                      {loteConsultaData.totalDefeitos > 0 && (
                        <span className="flex items-center gap-1.5 text-sm bg-red-100 text-red-700 px-3 py-1.5 rounded-full font-medium">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {loteConsultaData.totalDefeitos} defeitos
                        </span>
                      )}
                      {loteConsultaData.totalArranjos > 0 && (
                        <span className="flex items-center gap-1.5 text-sm bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full font-medium">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {loteConsultaData.totalArranjos} para arranjo
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-stone-500 mt-2">
                      Quantidade disponível ajustada:{' '}
                      <strong>
                        {Math.max(
                          0,
                          (loteConsultaData.lote?.quantidade_ajustada || loteConsultaData.lote?.quantidade || 0) -
                            (loteConsultaData.totalDefeitos || 0) -
                            (loteConsultaData.totalArranjos || 0)
                        )}{' '}
                        un.
                      </strong>
                    </p>
                  </div>
                )}
              </div>

              {/* SECÇÃO DE RASTREABILIDADE — apenas para lotes ARR */}
              {isArranjo && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50/50 p-4" data-testid="rastreabilidade-arranjo-section">
                  <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <Wrench className="h-5 w-5" />
                    Rastreabilidade das Peças a Recuperar
                  </h4>
                  
                  {loadingRast && (
                    <div className="text-center py-4 text-stone-400 text-sm">A carregar rastreabilidade...</div>
                  )}
                  
                  {!loadingRast && rastreabilidade && rastreabilidade.fontes.length > 0 && (
                    <>
                      <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                        <div className="p-2 rounded bg-white text-center">
                          <p className="text-stone-500">Peças</p>
                          <p className="text-lg font-bold text-red-700">{rastreabilidade.total_pecas}</p>
                        </div>
                        <div className="p-2 rounded bg-white text-center">
                          <p className="text-stone-500">Fontes</p>
                          <p className="text-lg font-bold text-stone-700">{rastreabilidade.num_fontes}</p>
                        </div>
                        <div className="p-2 rounded bg-white text-center">
                          <p className="text-stone-500">Tempo Total</p>
                          <p className="text-lg font-bold text-amber-700">{rastreabilidade.tempo_total_recuperacao_min}min</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {rastreabilidade.fontes.map((f, idx) => (
                          <div key={idx} className="bg-white border border-red-100 rounded-lg p-3" data-testid={`fonte-rastreio-${idx}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 rounded bg-red-600 text-white font-bold text-sm">
                                  {f.qtd} un
                                </span>
                                <span className="font-mono text-xs px-2 py-0.5 rounded bg-stone-100 text-stone-700 font-semibold">
                                  {f.lote_origem_codigo || '-'}
                                </span>
                              </div>
                              <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-semibold">
                                <Clock className="h-3 w-3" />
                                {f.tempo_recuperacao_min}min
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 gap-1.5 text-xs">
                              <div className="flex items-center gap-1.5">
                                <Layers className="h-3 w-3 text-blue-600" />
                                <span className="text-stone-500">Falhou em:</span>
                                <span className="font-mono font-bold text-primary">{f.operacao_origem_codigo}</span>
                                <span className="text-stone-700 truncate">{f.operacao_origem_nome}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <User className="h-3 w-3 text-stone-500" />
                                <span className="text-stone-700">{f.operador_nome || '-'}</span>
                                <span className="text-stone-400">·</span>
                                <span className="text-stone-500">
                                  {f.criado_em ? new Date(f.criado_em).toLocaleDateString('pt-PT', { day:'2-digit', month:'2-digit', year:'2-digit' }) : '-'}
                                </span>
                              </div>
                              {f.ops_em_falta && f.ops_em_falta.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1 mt-1">
                                  <span className="text-[10px] text-stone-500">Operações em falta:</span>
                                  {f.ops_em_falta.slice(0, 6).map((op, oi) => (
                                    <span key={oi} className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200">
                                      {op.codigo}
                                    </span>
                                  ))}
                                  {f.ops_em_falta.length > 6 && (
                                    <span className="text-[10px] text-stone-400">+{f.ops_em_falta.length - 6}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  
                  {!loadingRast && (!rastreabilidade || rastreabilidade.fontes.length === 0) && (
                    <p className="text-center py-4 text-stone-400 text-sm">
                      Sem informação de rastreabilidade disponível para este lote.
                    </p>
                  )}
                </div>
              )}

              {/* Lista de Operações Realizadas */}
              <div>
                <h4 className="font-semibold text-navy mb-3">Operações Realizadas</h4>
                {loteConsultaData.registos.length === 0 ? (
                  <p className="text-stone-500 text-center py-4">
                    Nenhuma operação registada neste lote.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {loteConsultaData.registos.map((registo, idx) => (
                      <div
                        key={registo.id || idx}
                        className="bg-white border border-stone-200 rounded-xl p-3"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-navy">
                            <span className="text-primary font-mono text-sm mr-1">{registo.operacao_codigo}</span>
                            {registo.operacao_nome}
                          </span>
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                            {registo.quantidade_produzida || 0} un.
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-stone-500">
                          <span>
                            <Clock className="h-3 w-3 inline mr-1" />
                            {registo.inicio ? new Date(registo.inicio).toLocaleString('pt-PT', { timeStyle: 'short' }) : '-'}
                            {' → '}
                            {registo.fim ? new Date(registo.fim).toLocaleString('pt-PT', { timeStyle: 'short' }) : '-'}
                          </span>
                          <span className="capitalize">
                            {registo.tipo_conclusao === 'total' ? '✓ Total' :
                              registo.tipo_conclusao === 'parcial' ? '⟳ Parcial' :
                              registo.tipo_conclusao || ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-stone-500 text-center py-8">
              Selecione um lote para consultar
            </p>
          )}
        </div>

        <div className="p-4 border-t border-stone-100">
          <Button
            onClick={handleClose}
            className="w-full h-12 rounded-xl"
            variant="outline"
          >
            Fechar
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
